/**
 * Browser-friendly stub implementation of `node-datachannel`.
 */

export type DescriptionType = string;
export type RtcConfig = Record<string, unknown>;

export type DataChannelInitConfig = {
  negotiated?: boolean;
  unordered?: boolean;
  maxRetransmits?: number;
};

class MockDataChannel {
  private open = true;
  private openHandlers: Array<() => void> = [];
  private closeHandlers: Array<() => void> = [];
  private messageHandlers: Array<(msg: unknown) => void> = [];
  private errorHandlers: Array<(err: string) => void> = [];

  constructor(public readonly label: string) {
    queueMicrotask(() => {
      this.openHandlers.forEach((handler) => handler());
    });
  }

  onOpen(handler: () => void): void {
    this.openHandlers.push(handler);
  }

  onClosed(handler: () => void): void {
    this.closeHandlers.push(handler);
  }

  onMessage(handler: (msg: unknown) => void): void {
    this.messageHandlers.push(handler);
  }

  onError(handler: (err: string) => void): void {
    this.errorHandlers.push(handler);
  }

  isOpen(): boolean {
    return this.open;
  }

  sendMessage(_message: string | ArrayBuffer | Buffer): void {
    // no-op
  }

  close(): void {
    if (!this.open) return;
    this.open = false;
    this.closeHandlers.forEach((handler) => handler());
  }
}

class MockPeerConnection {
  constructor(public readonly id: string, public readonly config: RtcConfig) {}

  onGatheringStateChange(handler: (state: string) => void): void {
    queueMicrotask(() => handler('complete'));
  }

  onLocalDescription(_handler: (sdp: string, type: DescriptionType) => void): void {
    // No-op in browser mock
  }

  onLocalCandidate(_handler: (candidate: string, mid: string) => void): void {
    // No-op in browser mock
  }

  createDataChannel(label: string, _init?: DataChannelInitConfig): MockDataChannel {
    return new MockDataChannel(label);
  }

  setRemoteDescription(_sdp: string, _type: DescriptionType): Promise<void> {
    return Promise.resolve();
  }

  addRemoteCandidate(_candidate: string, _mid?: string): Promise<void> {
    return Promise.resolve();
  }

  close(): void {
    // no-op
  }
}

export { MockPeerConnection as PeerConnection, MockDataChannel as DataChannel };

export const cleanup = (): void => {
  // noop cleanup
};

export default {
  PeerConnection: MockPeerConnection,
  DataChannel: MockDataChannel,
  cleanup,
};
